import { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, Eye, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';

interface DailyData {
  date: string;
  count: number;
  total_amount: number;
}

interface WalletChargeDailyProps {
  locationId: string;
  category: string;
  dateRange: { start: string; end: string };
  onBack: () => void;
  onViewTransactions: (date: string) => void;
}

const ROWS_PER_PAGE_OPTIONS = [10, 20, 50, 100];

/**
 * Wallet Charge Daily Breakdown View (Level 2)
 * Shows daily breakdown for a specific wallet charge category
 */
export function WalletChargeDaily({
  locationId,
  category,
  dateRange,
  onBack,
  onViewTransactions,
}: WalletChargeDailyProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<DailyData[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);

  // Pagination state
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchDailyData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/billing/${locationId}/wallet-charges-daily?category=${category}&start=${dateRange.start}&end=${dateRange.end}`,
        { credentials: 'include' }
      );
      const data = (await response.json()) as {
        success: boolean;
        displayName?: string;
        days?: DailyData[];
        total_count?: number;
        total_amount?: number;
        error?: string;
      };
      if (data.success) {
        setDays(data.days || []);
        setDisplayName(data.displayName || category);
        setTotalCount(data.total_count || 0);
        setTotalAmount(data.total_amount || 0);
      } else {
        setError(data.error || 'Failed to load daily breakdown');
      }
    } catch (err) {
      console.error('[WalletChargeDaily] Fetch error:', err);
      setError('Failed to load daily breakdown');
    } finally {
      setLoading(false);
    }
  }, [locationId, category, dateRange]);

  useEffect(() => {
    fetchDailyData();
  }, [fetchDailyData]);

  // Reset to page 1 when data or rowsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [days.length, rowsPerPage]);

  // Pagination calculations
  const totalPages = Math.ceil(days.length / rowsPerPage);
  const paginatedDays = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return days.slice(startIndex, startIndex + rowsPerPage);
  }, [days, currentPage, rowsPerPage]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatDateRange = () => {
    const startDate = new Date(dateRange.start + 'T00:00:00');
    const endDate = new Date(dateRange.end + 'T00:00:00');
    const startStr = startDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endStr = endDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
    return `${startStr} - ${endStr}`;
  };

  const formatCurrency = (amount: number) => {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(Math.abs(amount));
    return amount >= 0 ? `+${formatted}` : `-${formatted}`;
  };

  const formatCount = (count: number) => {
    return count.toLocaleString();
  };

  // Generate page numbers to show
  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600 mb-4">{error}</p>
        <button
          onClick={fetchDailyData}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header with Title and Breadcrumb */}
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-bold text-gray-900">Daily Breakdown</h2>
        <nav className="text-sm">
          <button
            onClick={onBack}
            className="text-blue-600 hover:underline"
          >
            Wallet Charges
          </button>
          <span className="mx-2 text-gray-400">&rsaquo;</span>
          <span className="font-semibold text-gray-900">{displayName}</span>
        </nav>
      </div>

      {/* Context Header (Blue Box) */}
      <div className="bg-sky-50 border-2 border-sky-500 rounded-lg p-4 mb-4 flex justify-between items-center">
        <div>
          <h3 className="text-base font-bold text-sky-700">{displayName}</h3>
          <p className="text-sm text-gray-600">{formatDateRange()}</p>
        </div>
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-gray-50 rounded text-sm font-medium hover:bg-gray-100 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Summary
        </button>
      </div>

      {/* Controls Row */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-700">Show</span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
            className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {ROWS_PER_PAGE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <span className="text-sm font-semibold text-gray-700">rows</span>
        </div>
        <span className="text-gray-400">|</span>
        <span className="text-sm font-semibold text-gray-700">
          Total: {formatCount(totalCount)} transactions = {formatCurrency(totalAmount)}
        </span>
      </div>

      {/* Daily Table */}
      <div className="border-2 border-gray-300 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-5 py-3 text-left text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Date
              </th>
              <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Transactions
              </th>
              <th className="px-5 py-3 text-right text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Amount
              </th>
              <th className="px-5 py-3 text-center text-sm font-semibold text-gray-600 border-b-2 border-gray-300">
                Details
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedDays.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-gray-500">
                  No data for selected period
                </td>
              </tr>
            ) : (
              paginatedDays.map((day) => {
                const isNegative = day.total_amount < 0;
                return (
                  <tr key={day.date} className="hover:bg-gray-50">
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-gray-900">
                      {formatDate(day.date)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right text-gray-900">
                      {formatCount(day.count)}
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-sm text-right">
                      <span className={`font-medium ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(day.total_amount)}
                      </span>
                    </td>
                    <td className="px-5 py-3 border-b border-gray-200 text-center">
                      <button
                        onClick={() => onViewTransactions(day.date)}
                        className="inline-flex items-center justify-center w-9 h-9 border-2 border-blue-500 rounded text-blue-500 hover:bg-blue-500 hover:text-white transition-colors"
                        title="View transactions"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {days.length > 0 && (
        <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
          <div className="text-sm text-gray-600">
            Showing {Math.min((currentPage - 1) * rowsPerPage + 1, days.length)} -{' '}
            {Math.min(currentPage * rowsPerPage, days.length)} of {days.length} days
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-3 py-1.5 text-sm text-gray-500">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page as number)}
                  className={`px-3 py-1.5 border rounded text-sm font-medium ${
                    currentPage === page
                      ? 'bg-gray-900 text-white border-gray-900'
                      : 'border-gray-300 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              )
            )}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-3 py-1.5 border border-gray-300 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
